/*
 * Copyright Evollis 2021
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of Evollis.
 * The intellectual and technical concepts contained herein are
 * proprietary to Evollis and are protected by trade secret and copyright law.
 * Dissemination of this information or reproduction of this material
 * is STRICTLY FORBIDDEN unless prior written permission is obtained
 * from Evollis.
 */
import { loc } from "../../../core/utils"
import { contractTypeEnum } from "../constants/ClientPageConstants"
import emojiRegex from "emoji-regex"
import * as yup from "yup"

export const requiredMessage = () => loc`Required`
export const selectNotCorrectValue = () => loc`Please pick a correct value`
const maximumCharactersMessage = (maximumValue) => loc`Maximum` + " " + maximumValue + " " + loc`characters`
const regexMessage = () => loc`Unauthorized characters`

// regex
const birthCityOnlyLetterRegex = /[A-Za-z]/

// A choice has been made here : code reusability or performance : if we compose the regex dynamically perfs are affected.
// If perf problems are detected for validation, change this implementation
const specialCharaterRegex = /[!@#$%^&*()+={}':"\\|<>/?]+/
const specialCharaterAdressRegex = /[!@#$%^&*()+={}:\\|<>/?]+/

const schema = yup.object().shape({
  identity: yup.object().shape({
    title: yup.string().nullable().required(requiredMessage),
    birthCountry: yup.string().nullable().required(requiredMessage),
    birthDepartment: yup.string().nullable().required(requiredMessage),
    birthCity: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "birthCity verification",
        message: "",
        test(value) {
          const regex = emojiRegex()
          if (!value) {
            return this.createError({
              message: selectNotCorrectValue,
              path: "identity.birthCity"
            })
          }
          if (specialCharaterRegex.test(value)) {
            return this.createError({
              message: regexMessage,
              path: "identity.birthCity"
            })
          }
          if (value?.match(regex) || !value?.match(birthCityOnlyLetterRegex)) {
            return this.createError({
              message: regexMessage,
              path: "identity.birthCity"
            })
          }
        }
      })
  }),
  coordinates: yup.object().shape({
    address: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .max(32, () => maximumCharactersMessage(32))
      .test({
        name: "address verification",
        message: "",
        test(value) {
          const regex = emojiRegex()
          if (!value) {
            return this.createError({
              message: requiredMessage,
              path: "coordinates.address"
            })
          }
          if (specialCharaterAdressRegex.test(value)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.address"
            })
          }
          if (value?.match(regex)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.address"
            })
          }
        }
      }),
    additionalAddress: yup
      .string()
      .nullable()
      .max(32, () => maximumCharactersMessage(32))
      .test({
        name: "additionalAddress verification",
        message: "",
        test(value) {
          const regex = emojiRegex()
          if (specialCharaterAdressRegex.test(value)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.additionalAddress"
            })
          }
          if (value?.match(regex)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.additionalAddress"
            })
          }
        }
      }),
    locality: yup
      .string()
      .nullable()
      .max(32, () => maximumCharactersMessage(32))
      .test({
        name: "locality verification",
        message: "",
        test(value) {
          const regex = emojiRegex()
          if (specialCharaterAdressRegex.test(value)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.locality"
            })
          }
          if (value?.match(regex)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.locality"
            })
          }
        }
      }),
    city: yup
      .string()
      .nullable()
      .required(requiredMessage)
      .test({
        name: "city verification",
        message: "",
        test(value) {
          const regex = emojiRegex()
          let postalCode = this.options.context.coordinates.postalCode
          if (!value) {
            return this.createError({
              message: selectNotCorrectValue,
              path: "coordinates.city"
            })
          }
          if (!postalCode) {
            return this.createError({
              message: selectNotCorrectValue,
              path: "coordinates.city"
            })
          }
          if (specialCharaterRegex.test(value)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.city"
            })
          }
          if (value?.match(regex)) {
            return this.createError({
              message: regexMessage,
              path: "coordinates.city"
            })
          }
        }
      })
  }),
  situation: yup.object().shape({
    endContractDate: yup
      .string()
      .nullable()
      .test({
        name: "endContractDate",
        message: "",
        test(value) {
          let workingSituation = this.options.context.situation.workingSituation
          if (workingSituation?.value === contractTypeEnum && !value) {
            return this.createError({
              message: requiredMessage,
              path: "situation.endContractDate"
            })
          }
          return true
        }
      }),
    housingType: yup.string().nullable().required(requiredMessage),
    maritalStatus: yup.string().nullable().required(requiredMessage)
  })
})

export default schema //(values) => schema1.validate(values, { abortEarly: false, context: values });
